import { Reports } from '@/app/models/costTrackingTotal';
import { useUpdateTotalityMutation } from '@/app/services/costTrackingApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReportItem from './ReportItem';

interface NewEditRubricHeaderProps {
  selectItem: Reports[];
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditRubricHeader(props: NewEditRubricHeaderProps) {
  const { t } = useTranslation(['costTracking', 'common']);
  const theme = useTheme();
  const { selectItem, setDrawerOpen } = props;

  const [reports, setReports] = useState(() => {
    const reportSections = [
      { name: 'baseline', type: 'BASELINE' },
      { name: 'total.forecasted', type: 'REPLANNED' },
      { name: 'total.fulfilled', type: 'FULFILLED' },
      { name: 'total.fulfilled.plus.forecasted', type: 'PAYBOOK' },
      { name: 'difference', type: 'DIFFERENCE' },
    ];

    return reportSections.map((section, index) => {
      const report = (selectItem || []).find((r) => r.planningReportType === section.type);
      return {
        id: report?.id || index,
        name: section.name,
        value: report?.value || 0,
        justify: report?.justify || '',
        planningReportType: section.type,
      };
    });
  });

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleJustificationChange = (index: number, newValue: string) => {
    const updatedReports = [...reports];
    updatedReports[index].justify = newValue;
    setReports(updatedReports);
  };

  const [update] = useUpdateTotalityMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        list: reports.map((report) => ({
          id: report.id,
          justify: report.justify,
        })),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:comments.saved.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <ModalPageTitle title={t('comment.rubric.values')} />
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mb: 2 }} />

        {reports.map((item, index) => (
          <ReportItem
            key={item.id}
            name={t(item.name)}
            value={item.value}
            justify={item.justify as string}
            index={index}
            isLast={index === reports.length - 1}
            onJustifyChangeWithIndex={handleJustificationChange}
            header
          />
        ))}
      </Box>

      <Box sx={{ mt: 1 }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} />
      </Box>
    </Box>
  );
}
